import React, { useEffect, useState, createElement } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { ScrollToTop } from './components/layout/ScrollToTop';
import { InquiryModal } from './components/forms/InquiryModal';
import { HomePage } from './pages/HomePage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { ApplicationsPage } from './pages/ApplicationsPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { LegalPage } from './pages/LegalPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { ShippingPaymentPage } from './pages/ShippingPaymentPage';
import { ReturnsRefundsPage } from './pages/ReturnsRefundsPage';
import { ServicePage } from './pages/ServicePage';
import { GalleryPage } from './pages/GalleryPage';
import { FAQPage } from './pages/FAQPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { LanguageProvider } from './lib/i18n';
import { WhatsAppButton } from './components/layout/WhatsAppButton';
import { CookieBanner } from './components/layout/CookieBanner';
import { BackToTop } from './components/layout/BackToTop';
export function App() {
  useEffect(() => {
    const title = 'D-TECH | Električna kolica i sistemi za transport tereta';
    const description = 'Profesionalni stepenišni penjači i električna kolica za bezbedan transport tereta do 250kg. Uštedite na radnoj snazi i sprečite povrede. Direktan uvoz iz proizvodnje.';
    const ogImage = 'https://cdn.magicpatterns.com/uploads/mrmC3EPvZ6yt8zBvajm1cp/LOGO_FINAL.jpg';
    const siteUrl = 'https://dtech.rs';
    document.title = title;
    const upsertMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    // Standard SEO
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', 'električna kolica, stepenišni penjač, transport tereta, D-TECH, električni penjač, SCA, SCB, RAPTOR, transport uz stepenice, industrijska kolica');
    upsertMeta('name', 'robots', 'index, follow');
    // Open Graph (Viber, WhatsApp, Facebook, LinkedIn previews)
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:url', siteUrl);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:locale', 'sr_RS');
    upsertMeta('property', 'og:site_name', 'D-TECH Solutions');
    // Twitter Card
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', ogImage);
    // Favicon (browser tab icon)
    const faviconUrl = 'https://cdn.magicpatterns.com/uploads/cPqTf1LJZhBwCZDLYdDrYg/LOOG_GMAIL.jpg';
    const upsertLink = (rel: string, href: string) => {
      let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };
    upsertLink('icon', faviconUrl);
    upsertLink('shortcut icon', faviconUrl);
    upsertLink('apple-touch-icon', faviconUrl);
    const returnPolicy = {
      '@type': 'MerchantReturnPolicy',
      url: 'https://dtech.rs/reklamacije',
      applicableCountry: 'RS',
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/ReturnShippingFees'
    };
    const shippingDetails = {
      '@type': 'OfferShippingDetails',
      shippingSettingsLink: 'https://dtech.rs/isporuka-i-placanje',
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: 'RS'
      }
    };
    // JSON-LD Schema
    const schemaData = {
      '@context': 'https://schema.org',
      '@graph': [{
        '@type': 'Organization',
        '@id': 'https://dtech.rs/#organization',
        name: 'D-TECH Solutions',
        url: 'https://dtech.rs',
        logo: 'https://cdn.magicpatterns.com/uploads/mrmC3EPvZ6yt8zBvajm1cp/LOGO_FINAL.jpg',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+381-65-331-2811',
          contactType: 'customer service',
          email: 'info@dtech.rs',
          availableLanguage: ['Serbian', 'English']
        },
        sameAs: ['https://www.instagram.com/dtech.rs', 'https://www.facebook.com/profile.php?id=61589561484063', 'https://www.tiktok.com/@dtech_rs', 'https://www.linkedin.com/company/115805045/']
      }, {
        '@type': 'Product',
        name: 'D-TECH SCA',
        image: 'https://cdn.magicpatterns.com/uploads/rf5NrgHygwqJiVRF6Ubzv8/SCA_za_sajt.jpg',
        description: 'Industrijska verzija za teške uslove rada. Stabilna na grubim stepenicama i podlogama zahvaljujući dva pogonska točka i zupčastom mehanizmu.',
        brand: {
          '@type': 'Brand',
          name: 'D-TECH'
        },
        mpn: 'SCA-STANDARD',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: '1990',
          availability: 'https://schema.org/PreOrder',
          url: 'https://dtech.rs/products/sca',
          hasMerchantReturnPolicy: returnPolicy,
          shippingDetails: shippingDetails
        }
      }, {
        '@type': 'Product',
        name: 'D-TECH SCB (STANDARD)',
        image: 'https://cdn.magicpatterns.com/uploads/2JFxrQoz9bUYCTY3p3fnnv/DTECH_STANDARD.jpg',
        description: 'Osnovna konfiguracija za svakodnevnu profesionalnu upotrebu. Pouzdan transport tereta uz stepenice.',
        brand: {
          '@type': 'Brand',
          name: 'D-TECH'
        },
        mpn: 'SCB-STANDARD',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: '2190',
          availability: 'https://schema.org/PreOrder',
          url: 'https://dtech.rs/products/standard',
          hasMerchantReturnPolicy: returnPolicy,
          shippingDetails: shippingDetails
        }
      }, {
        '@type': 'Product',
        name: 'D-TECH SCB (STANDARD+)',
        image: 'https://cdn.magicpatterns.com/uploads/m5nEPzJD6tLEnedUiegTe9/basic_1.jpg',
        description: 'Standard sa dodatnim zaštitnim neklizajućim slojem za povećanu stabilnost u zahtevnim uslovima.',
        brand: {
          '@type': 'Brand',
          name: 'D-TECH'
        },
        mpn: 'SCB-STANDARD-PLUS',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: '2290',
          availability: 'https://schema.org/PreOrder',
          url: 'https://dtech.rs/products/standard-plus',
          hasMerchantReturnPolicy: returnPolicy,
          shippingDetails: shippingDetails
        }
      }, {
        '@type': 'Product',
        name: 'D-TECH RAPTOR (HEAVY DUTY)',
        image: 'https://cdn.magicpatterns.com/uploads/qLkakcP4DrV2Dp874ZqZ5Q/RPT.jpg',
        description: 'Premium konfiguracija sa zaštitnom bojom otpornom na habanje i dve baterije za produženi rad.',
        brand: {
          '@type': 'Brand',
          name: 'D-TECH'
        },
        mpn: 'RAPTOR',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: '2390',
          availability: 'https://schema.org/PreOrder',
          url: 'https://dtech.rs/products/raptor',
          hasMerchantReturnPolicy: returnPolicy,
          shippingDetails: shippingDetails
        }
      }, {
        '@type': 'Product',
        name: 'D-TECH SCC (PRO)',
        image: 'https://cdn.magicpatterns.com/uploads/hggMGv5HaYSpxFwXnwmnHo/SCC_PRO.png',
        description: 'Unapređena mašina za penjanje uz stepenice sa malim točkovima i hidrauličnom/elektromagnetnom potpornom šipkom.',
        brand: {
          '@type': 'Brand',
          name: 'D-TECH'
        },
        mpn: 'SCC-PRO',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: '0',
          availability: 'https://schema.org/PreOrder',
          url: 'https://dtech.rs/products/scc-pro',
          hasMerchantReturnPolicy: returnPolicy,
          shippingDetails: shippingDetails
        }
      }]
    };
    let scriptEl = document.head.querySelector<HTMLScriptElement>('script[type="application/ld+json"]');
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptEl);
    }
    scriptEl.textContent = JSON.stringify(schemaData);
    // Google Analytics (GA4) - G-5F0246P87J
    const GA_ID = 'G-5F0246P87J';
    if (!document.head.querySelector(`script[data-ga-loader="true"]`)) {
      const gaLoader = document.createElement('script');
      gaLoader.async = true;
      gaLoader.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      gaLoader.setAttribute('data-ga-loader', 'true');
      document.head.appendChild(gaLoader);
    }
    if (!document.head.querySelector(`script[data-ga-init="true"]`)) {
      const gaInit = document.createElement('script');
      gaInit.setAttribute('data-ga-init', 'true');
      gaInit.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', '${GA_ID}');
      `;
      document.head.appendChild(gaInit);
    }
  }, []);
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
  const [preselectedProductId, setPreselectedProductId] = useState<string | undefined>();
  const handleRequestQuote = (productId?: string) => {
    setPreselectedProductId(productId);
    setIsInquiryModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsInquiryModalOpen(false);
    setPreselectedProductId(undefined);
  };
  return <LanguageProvider data-id="element-0">
      <BrowserRouter data-id="element-1">
        <ScrollToTop data-id="element-2" />
        <div className="min-h-screen flex flex-col bg-dark-bg" data-id="element-3">
          <Header onRequestQuote={() => handleRequestQuote()} data-id="element-4" />
          <Routes data-id="element-5">
            <Route path="/" element={<HomePage onRequestQuote={() => handleRequestQuote()} data-id="element-7" />} data-id="element-6" />
            <Route path="/products" element={<ProductsPage onRequestQuote={() => handleRequestQuote()} data-id="element-9" />} data-id="element-8" />
            <Route path="/products/:slug" element={<ProductDetailPage data-id="element-11" />} data-id="element-10" />
            <Route path="/applications" element={<ApplicationsPage data-id="element-13" />} data-id="element-12" />
            <Route path="/about" element={<AboutPage data-id="element-15" />} data-id="element-14" />
            <Route path="/contact" element={<ContactPage data-id="element-17" />} data-id="element-16" />
            <Route path="/legal" element={<LegalPage data-id="element-19" />} data-id="element-18" />
            <Route path="/politika-privatnosti" element={<PrivacyPolicyPage data-id="element-21" />} data-id="element-20" />
            <Route path="/uslovi-koriscenja" element={<TermsOfServicePage data-id="element-23" />} data-id="element-22" />
            <Route path="/isporuka-i-placanje" element={<ShippingPaymentPage data-id="element-25" />} data-id="element-24" />
            <Route path="/reklamacije" element={<ReturnsRefundsPage data-id="element-27" />} data-id="element-26" />
            <Route path="/servis" element={<ServicePage data-id="element-29" />} data-id="element-28" />
            <Route path="/gallery" element={<GalleryPage data-id="element-31" />} data-id="element-30" />
            <Route path="/faq" element={<FAQPage data-id="element-33" />} data-id="element-32" />
            <Route path="*" element={<NotFoundPage data-id="element-35" />} data-id="element-34" />
          </Routes>
          <Footer data-id="element-36" />
          <InquiryModal isOpen={isInquiryModalOpen} onClose={handleCloseModal} preselectedProductId={preselectedProductId} data-id="element-37" />
          <WhatsAppButton data-id="element-38" />
          <BackToTop data-id="element-39" />
          <CookieBanner data-id="element-40" />
        </div>
      </BrowserRouter>
    </LanguageProvider>;
}